import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDownLeft, ArrowUpRight, Plus, Check, X, HandCoins } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageHeader, FullLoader, EmptyState, Spinner } from "../components/common";
import PinModal from "../components/PinModal";

function Avatar({ src, name, size = 44 }) {
  return src ? <img src={src} alt="" className="rounded-full object-cover" style={{ width: size, height: size }} />
    : <div className="grid place-items-center rounded-full bg-primary/15 text-primary font-semibold shrink-0" style={{ width: size, height: size }}>{(name || "?").charAt(0).toUpperCase()}</div>;
}

export default function Requests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const nav = useNavigate();
  const [tab, setTab] = useState(location.state?.tab || "incoming");
  const [busyId, setBusyId] = useState(null);
  const [pinRequired, setPinRequired] = useState(false);
  const [payPin, setPayPin] = useState(null); // request pending PIN confirmation

  const load = async () => {
    try { const { data } = await api.get("/requests"); setItems(data); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/security/pin").then(({ data }) => setPinRequired(data.pin_set && data.pin_enabled)).catch(() => {}); }, []);

  const doPay = async (r) => {
    setBusyId(r.request_id);
    try {
      await api.post(`/requests/${r.request_id}/pay`, {}, { headers: { "Idempotency-Key": `req_${r.request_id}` } });
      toast.success("Paid");
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusyId(null); }
  };

  const act = async (r, kind) => {
    if (kind === "pay") {
      if (pinRequired) { setPayPin(r); return; }
      return doPay(r);
    }
    setBusyId(r.request_id);
    try {
      await api.post(`/requests/${r.request_id}/decline`);
      toast.success("Done");
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusyId(null); }
  };

  const shown = items.filter((r) => r.direction === tab);

  return (
    <div className="animate-fade-up">
      <PageHeader title="Requests" subtitle="Money you owe and money owed to you"
        action={<button data-testid="new-request-btn" onClick={() => nav("/app/request")} className="grid place-items-center w-10 h-10 rounded-full bg-primary text-primary-foreground"><Plus size={20} /></button>} />

      <div className="flex gap-2 mb-4">
        {[["incoming", "To pay"], ["outgoing", "Requested"]].map(([id, label]) => (
          <button key={id} data-testid={`req-tab-${id}`} onClick={() => setTab(id)}
            className={`px-4 py-2 rounded-full text-sm font-medium ${tab === id ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>{label}</button>
        ))}
      </div>

      {loading ? <FullLoader /> : shown.length === 0 ? (
        <EmptyState icon={HandCoins} title="No requests" body={tab === "incoming" ? "Requests for you to pay will appear here." : "Request money from anyone with their Cash Tag."}>
          <button data-testid="empty-new-request-btn" onClick={() => nav("/app/request")} className="h-12 px-6 rounded-2xl bg-primary text-primary-foreground font-semibold">Request money</button>
        </EmptyState>
      ) : (
        <div className="space-y-3">
          {shown.map((r) => {
            const who = tab === "incoming" ? { name: r.requester_name, tag: r.requester_cash_tag, photo: r.requester_photo } : { name: r.payer_name, tag: r.payer_cash_tag };
            return (
              <div key={r.request_id} data-testid={`request-${r.request_id}`} className="rounded-2xl bg-card border border-border p-4">
                <div className="flex items-center gap-3">
                  <Avatar src={who.photo} name={who.name} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{who.name} <span className="text-xs text-muted-foreground font-mono">{who.tag}</span></div>
                    <div className="text-xs text-muted-foreground">{r.note || (r.split_id ? "Bill split" : "Payment request")} · <span className="capitalize">{r.status}</span></div>
                  </div>
                  <div className="font-semibold font-mono">{r.amount_display}</div>
                </div>
                {r.status === "pending" && tab === "incoming" && (
                  <div className="flex gap-2 mt-3">
                    <button data-testid={`decline-${r.request_id}`} onClick={() => act(r, "decline")} disabled={busyId === r.request_id} className="flex-1 h-11 rounded-xl bg-secondary font-semibold text-sm flex items-center justify-center gap-1"><X size={16} /> Decline</button>
                    <button data-testid={`pay-${r.request_id}`} onClick={() => act(r, "pay")} disabled={busyId === r.request_id} className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground font-semibold text-sm flex items-center justify-center gap-1">{busyId === r.request_id ? <Spinner className="w-4 h-4" /> : <><Check size={16} /> Pay {r.amount_display}</>}</button>
                  </div>
                )}
                {r.status === "pending" && tab === "outgoing" && (
                  <button data-testid={`cancel-${r.request_id}`} onClick={() => act(r, "decline")} disabled={busyId === r.request_id} className="w-full h-10 mt-3 rounded-xl bg-secondary font-semibold text-sm">Cancel request</button>
                )}
              </div>
            );
          })}
        </div>
      )}
      <PinModal open={!!payPin} onClose={() => setPayPin(null)}
        onSuccess={() => { const r = payPin; setPayPin(null); doPay(r); }}
        title="Confirm with your PIN" subtitle={payPin ? `Paying ${payPin.amount_display}` : ""} />
    </div>
  );
}

